/**
 * Page 1 — Overview.
 *
 * The sliced bar chart is the interactive core: break down by any dimension, narrow
 * with the dropdowns, click a bar to select it. The month trend above follows both the
 * dropdowns and the picked bar (review #79); jumping elsewhere scoped to the slice is
 * the selection bar's explicit buttons. Bar length is claim LABOR; the right label is cost per claim. Colour is the
 * symmetric ratio to the org rate — one colour each way, no amber band.
 */
import { useCallback, useEffect, useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAppData } from '@/data/AppData'
import { useLoadedDataset } from '@/data/DatasetProvider'
import { GROUP_NOTE, hasModeled, useFilters } from '@/data/filters'
import { useOpenTouchDetail } from '@/data/touchDrill'
import { useOpenClaimDetail } from '@/data/claimDrill'
import { FacilityLink, useOpenFacility } from '@/components/FacilityLink'
import { NO_CLAIM, isNoClaim } from '@/data/noClaim'
import { TOP_BARS, barSortOf, barSortPhrase, nextBarSort, shownBars, sortBars } from '@/pages/overviewBars'
import { SliceLaborRows, type SliceCell } from '@/components/SliceLaborRows'
import { rowRangeShare, type Person } from '@/data/selectors'
import type { CrosstabDict } from '@/data/types'
import { ChartCard, LineChart, RankBars, Scrim, ratioColor, type RankBar } from '@/components/Charts'
import { DataTable, type Column } from '@/components/Table'
import { ExportBar } from '@/components/ExportBar'
import type { ExportColumn } from '@/ui/export'
import { PopOut } from '@/components/PopOut'
import { EmployeeMonthGrid, MONTH_METRIC_BY_KEY } from '@/components/EmployeeMonthGrid'
import {
  ALL_IN_SCOPE,
  departmentTrend,
  pooledTrend,
  sliceWeights,
  groupRows,
  trendTableRows,
  type MonthTotals,
  type SliceMatch,
  type TrendTableRow,
} from '@/measures/trend'
import { monthScorer } from '@/measures/monthScore'
import { COLLECTION_LABEL } from '@/measures/collection'
import { Select } from '@/components/Bits'
import { SearchPick } from '@/components/SearchPick'
import { DIMENSION_HELP } from '@/measures/definitions'
import { stateName } from '@/data/stateNames'
import { unattributedClaimLabor } from '@/measures/core'
import { sameValue, valueName } from '@/data/blankValue'
import { useUrlState, text, maybeText, oneOf, bool, sortSpec } from '@/ui/urlState'
import { money0, money2, moneyK, int, pct, dec } from '@/format'

const MONTH_LABEL = (m: string) =>
  new Date(`${m}-01T00:00:00`).toLocaleString('en-US', { month: 'short' })

/** Trend metric key → the same measure's key in the employee × month grid. */
const TREND_TO_GRID: Record<string, string> = {
  cpcb: 'cpcb',
  billed: 'billed',
  labor: 'labor',
  res: 'resolved',
  rvb: 'rvb',
  ntp: 'zeroTouch',
  tpr: 'touches',
  wk: 'wk',
  facm: 'facm',
  coll: 'coll',
}

/** The slicer's Department when the filter bar holds more than one. */
const SEVERAL = '__several__'

type Dim = 'facilityGroup' | 'department' | 'state' | 'payer' | 'facility'

const DIMS: { value: Dim; label: string; plural: string; idx: number; dict: keyof DictNames }[] = [
  { value: 'facilityGroup', label: 'Facility group', plural: 'facility groups', idx: 4, dict: 'facilityGroups' },
  { value: 'department', label: 'Department', plural: 'departments', idx: 1, dict: 'departments' },
  { value: 'state', label: 'State', plural: 'states', idx: 2, dict: 'states' },
  { value: 'payer', label: 'Payer type', plural: 'payer types', idx: 3, dict: 'payers' },
  { value: 'facility', label: 'Facility', plural: 'facilities', idx: 5, dict: 'facilities' },
]

interface DictNames {
  employees: string[]
  departments: string[]
  states: string[]
  payers: string[]
  facilityGroups: string[]
  facilities: string[]
}

export function Overview() {
  const dataset = useLoadedDataset()
  const { derived, scope, openPerson, coverage, label, crosstab, volume } = useAppData()
  const { set, filters } = useFilters()
  // The page's own settings live in the URL, so Back from a drill returns to them.
  const [trendMetric, setTrendMetric] = useUrlState('trend', 'cpcb', text)
  /** The trend drawn as lines or laid out as departments × months (review #858). */
  const [trendView, setTrendView] = useUrlState<'chart' | 'table'>(
    'trend.view',
    'chart',
    oneOf(['chart', 'table'] as const, 'chart'),
  )
  const navigate = useNavigate()
  const openTouches = useOpenTouchDetail()
  const openClaims = useOpenClaimDetail()
  const openFacility = useOpenFacility()

  const [dim, setDim] = useUrlState<Dim>(
    'by',
    'facilityGroup',
    oneOf(['facilityGroup', 'department', 'state', 'payer', 'facility'] as const, 'facilityGroup'),
  )
  /*
   * The slicer's Department IS the filter bar's Department (review #157, 2026-10-07).
   * It used to be a second control of its own, on the claim's department, while the
   * bar above narrowed by the person's: the two could read "All" and "HMO" at once,
   * and the trend — one line per department — kept every department's line under an
   * HMO slice. One control now, so the bars, the trend and the tiles all follow it.
   * An old link's `?dept=` is moved onto the filter once, below.
   */
  const [legacyDept, setLegacyDept] = useUrlState('dept', '', text)
  useEffect(() => {
    if (!legacyDept) return
    set('department', [legacyDept])
    setLegacyDept('')
  }, [legacyDept, set, setLegacyDept])
  const pageDept = filters.department
  const setPageDept = (d: string) => set('department', d ? [d] : [])
  const [narrowState, setNarrowState] = useUrlState('state', '', text)
  const [narrowPayer, setNarrowPayer] = useUrlState('payer', '', text)
  const [selected, setSelected] = useUrlState('pick', null, maybeText)
  /** Facility-group drill-down: when set, bars show facilities inside this group. */
  const [insideGroup, setInsideGroup] = useUrlState('group', null, maybeText)
  /** The picked bar's "Labor rows" panel — the cells behind it. In the URL so Back keeps it. */
  const [rowsOpen, setRowsOpen] = useUrlState('rows', false, bool)
  /** Every bar, not the largest TOP_BARS. The enlarged view always shows every bar. */
  const [showAllBars, setShowAllBars] = useUrlState('all', false, bool)
  /**
   * The bars' column sort (review #402): billed claims or cost per claim, either way.
   * Absent is the default, largest payroll labor first. In the URL like a table's sort.
   */
  const [rawBarSort, setRawBarSort] = useUrlState('bars.sort', null, sortSpec)
  const barSort = barSortOf(rawBarSort)

  // The claim filters' share of each person, as the tiles have it: the crosstab is not
  // split by claim, so its rows are scaled to it and the bars still add up to the tiles.
  const claimWeight = scope.claimShare

  const inScope = useMemo(
    () => new Set(scope.people.map((p) => p.employee)),
    [scope.people],
  )

  // The org rate the slice bars are coloured against. The page's own figure strip is
  // gone (the masthead carries it), so this is all the totals are for now.
  const totals = useMemo(() => {
    const e = scope.people
    const claimLabor = e.reduce((a, p) => a + p.claimLabor, 0)
    const billed = e.reduce((a, p) => a + p.claims, 0)
    return { costPerClaim: billed === 0 ? null : claimLabor / billed }
  }, [scope.people])

  /**
   * Unattributed labor: the labor of paid months that billed no claims, in cost per
   * claim all the same (MEASURE-RULES §3.2, §3.5). Payroll without claims lists it by
   * person and month and adds up to this, to the cent, for the same filters.
   */
  const unattributed = useMemo(
    () => ({
      labor: unattributedClaimLabor(scope.people),
      people: scope.people.filter((p) => p.unattributedLabor !== 0).length,
    }),
    [scope.people],
  )

  /* -------------------------------------------------- the sliced bar chart */

  const activeDim: Dim = insideGroup ? 'facility' : dim
  const dimDef = DIMS.find((d) => d.value === activeDim)!

  /**
   * The filter bar's Facility group filter, on the crosstab's own group column: the
   * bars select those rows exactly, rather than scaling each person by their share of
   * the groups as the tiles must (`GROUP_NOTE`). The two still add up — the share is
   * taken off these rows — and the no-claim bar drops out, having no facility.
   */
  const barGroups = useMemo(
    () => groupRows(crosstab, filters.facilityGroup),
    [crosstab, filters.facilityGroup],
  )

  /** A crosstab row inside the filter bar's facility groups and the slicer's narrowing. */
  const inNarrowing = useCallback(
    (row: CrosstabDict['rows'][number]) =>
      (!barGroups || barGroups(row)) &&
      (!narrowState || sameValue(crosstab.states[row[2]], narrowState)) &&
      (!narrowPayer || sameValue(crosstab.payers[row[3]], narrowPayer)) &&
      (!insideGroup || sameValue(crosstab.facilityGroups[row[4]], insideGroup)),
    [crosstab, barGroups, narrowState, narrowPayer, insideGroup],
  )

  /**
   * One bar per slice, longest labor first. A slice with labor but no claims is
   * drawn too, in grey with "—" for its rate, as the Ledger does — hiding it would
   * hide the unattributed pile from the one chart that ranks where labor goes.
   */
  const { bars, sliceCount, cells } = useMemo<{
    bars: (RankBar & { claims: number; cpc: number | null })[]
    sliceCount: number
    /** The picked bar's cells, scaled as the bar is: what "Labor rows" lists. */
    cells: SliceCell[]
  }>(() => {
    const ct = crosstab
    const names = ct[dimDef.dict] as string[]
    // By NAME, not dictionary index: every blank spelling ("", "Unknown", "UNKNOWN")
    // is one "Unknown" bar, and the drills below match it the same way (review #177).
    const cells: SliceCell[] = []
    const acc = new Map<
      string,
      { claims: number; labor: number; people: Set<number>; facs: Set<number> }
    >()

    for (const row of ct.rows) {
      const employee = ct.employees[row[0]]
      if (!employee || !inScope.has(employee)) continue
      // The crosstab is whole-period. Under a month range each person's rows are
      // scaled to their in-range share, so the bars add up to the cards above them.
      // Labor takes the share of its own kind (`rowRangeShare`): a no-claim row the
      // range's no-claim pay, any other row the labor of the months that billed claims.
      const person = derived.byName.get(employee)
      const share = rowRangeShare(person?.rangeShare, isNoClaim(ct.facilities[row[5]]))
      // The claim filters' weight, as the tiles take it, so the bars still add up to
      // them under a claim type, service type or status.
      const cw = person ? claimWeight(person.employee) : { claims: 1, labor: 1 }
      if (!inNarrowing(row)) continue

      // dimDef.idx is a runtime index into a fixed tuple, so it needs a widened view.
      const key = valueName(names[(row as readonly number[])[dimDef.idx]!])
      const e = acc.get(key) ?? {
        claims: 0,
        labor: 0,
        people: new Set<number>(),
        facs: new Set<number>(),
      }
      const claims = row[6] * share.claims * cw.claims
      const labor = row[7] * share.labor * cw.labor
      e.claims += claims
      e.labor += labor
      e.people.add(row[0])
      e.facs.add(row[5])
      acc.set(key, e)
      if (selected && key === selected) {
        cells.push({
          employee,
          department: ct.departments[row[1]] ?? 'Unknown',
          state: ct.states[row[2]] ?? 'Unknown',
          payer: ct.payers[row[3]] ?? 'Unknown',
          group: ct.facilityGroups[row[4]] ?? 'Unknown',
          facility: ct.facilities[row[5]] ?? 'UNKNOWN',
          claims,
          labor,
        })
      }
    }

    const items = [...acc.entries()]
      .map(([name, v]) => {
        const cpc = v.claims > 0 ? v.labor / v.claims : null
        // Payroll that landed on no claim: no facility, state or payer, and so no rate.
        // It is its own bar so it is never divided by somebody else's claims.
        const noClaim = isNoClaim(name)
        // What is left in "Unknown" is real claims whose facility did not resolve.
        const unresolved =
          (activeDim === 'facilityGroup' && name === 'Unknown') ||
          (activeDim === 'facility' && name.toUpperCase() === 'UNKNOWN')
        const tip: [string, string][] = noClaim
          ? [
              ['Payroll labor', moneyK(v.labor)],
              ['People', int(v.people.size)],
              ['What it is', 'Payroll on no claim — counted in every cost per claim, rated nowhere'],
            ]
          : [
              ['Cost per claim', cpc === null ? '—' : money2(cpc)],
              [label('Billed claims'), int(v.claims)],
              ['Payroll labor', moneyK(v.labor)],
              ['People', int(v.people.size)],
            ]
        if (activeDim === 'facilityGroup' && !noClaim) tip.push(['Facilities handled', int(v.facs.size)])
        if (unresolved) tip.push(['What it is', 'Claims whose facility did not resolve'])
        return {
          key: name,
          label:
            activeDim === 'state'
              ? stateName(name)
              : noClaim
                ? 'No claim (unattributed)'
                : unresolved
                  ? 'Unknown (facility not resolved)'
                  : name,
          value: v.labor,
          right: cpc === null ? '—' : money2(cpc),
          // The claims the rate divides by, beside it (review #231).
          count: int(v.claims),
          // The numbers behind `count` and `right`, for the column sort.
          claims: v.claims,
          cpc,
          color: ratioColor(cpc, totals.costPerClaim),
          tip,
        }
      })
      .sort((a, b) => b.value - a.value)

    // Which of them to draw — the cutoff, Show all, and the pinned Unknown and
    // no-claim bars — is shownBars', at render, where the enlarged view is known.
    return { bars: items, sliceCount: items.length, cells }
  }, [
    crosstab,
    derived.byName,
    claimWeight,
    dimDef,
    activeDim,
    inScope,
    inNarrowing,
    selected,
    totals.costPerClaim,
    label,
  ])

  /** The bars drawn: the largest TOP_BARS, or every one, and Unknown / no-claim under a rule. */
  const sortedBars = useMemo(() => sortBars(bars, barSort), [bars, barSort])
  const shownTop = useMemo(
    () => shownBars(sortedBars, { selected, showAll: showAllBars }),
    [sortedBars, selected, showAllBars],
  )
  const shownEvery = useMemo(() => shownBars(sortedBars, { selected, showAll: true }), [sortedBars, selected])

  /** People working the selected slice, for the table below. */
  const slicePeople = useMemo<Person[]>(() => {
    if (!selected) return scope.people
    const ct = crosstab
    const names = ct[dimDef.dict] as string[]
    const keep = new Set<string>()
    for (const row of ct.rows) {
      // The bar's own rows: its name (blanks as one "Unknown") inside the narrowing.
      if (valueName(names[(row as readonly number[])[dimDef.idx]!]) !== selected) continue
      if (!inNarrowing(row)) continue
      const e = ct.employees[row[0]]
      if (e) keep.add(e)
    }
    return scope.people.filter((p) => keep.has(p.employee))
  }, [selected, scope.people, crosstab, dimDef, inNarrowing])

  const claimFiltered = hasModeled(filters)
  const pickedNoClaim = isNoClaim(selected)
  /**
   * The no-claim bar's person-month rows, over the range, for the people in scope.
   * Null when another bar is picked or the bundle predates them.
   */
  const noClaimMonths = useMemo(() => {
    if (!pickedNoClaim || !dataset.unattributedMonths) return null
    const months = new Set(scope.months)
    return dataset.unattributedMonths.filter((r) => inScope.has(r.employee) && months.has(r.month))
  }, [pickedNoClaim, dataset.unattributedMonths, scope.months, inScope])

  const liveTitle = `Cost per claim by ${dimDef.label.toLowerCase()}`
  /** The Ledger's one-line caption: how many slices, what narrows them, what a click does. */
  const liveCaption = useMemo(() => {
    const n = sliceCount
    const narrows = [
      pageDept.join(', '),
      filters.facilityGroup.join(', '),
      narrowState && stateName(narrowState),
      narrowPayer,
      insideGroup,
    ].filter(Boolean)
    return [
      `${int(n)} ${n === 1 ? dimDef.label.toLowerCase() : dimDef.plural}`,
      narrows.length ? narrows.join(' · ') : 'whole book',
      // Say so when the months are an allocation: this table has no month column.
      ...(derived.narrowed ? ['months split by each person’s share'] : []),
      // The crosstab is not split by claim type, service type or status: under a claim
      // filter its rows take each person's filtered share, so the bars add up to the
      // tiles but each bar's mix is an allocation. Said here, as the months are.
      ...(claimFiltered ? ['claim filters split by each person’s share'] : []),
      'click a bar to show it in the trend',
    ].join(' · ')
  }, [
    sliceCount,
    dimDef,
    insideGroup,
    pageDept,
    filters.facilityGroup,
    narrowState,
    narrowPayer,
    derived.narrowed,
    claimFiltered,
  ])

  /* ------------------------------------------------- month lines by department */

  /**
   * The department trend, one line per department.
   *
   * This used to be locked to cost per RESOLVED claim, and it had to be: the month
   * grain carried no labor, so the chart ALLOCATED each person's period labor across
   * months by their share of resolved claims. It was an estimate of a measure nothing
   * in the app is scored on, drawn as if observed.
   *
   * The bundle now carries real claim labor, billed claims and facilities per
   * person-month, so every line here is observed. The default is cost per BILLED
   * claim, matching Shayna's artifact and the denominator the rest of the app uses.
   */
  const TREND_METRICS = useMemo(
    () => [
      {
        key: 'cpcb',
        // "Cost per weighted claim" while the Weighted claims switch is on.
        label: label('Cost per billed claim'),
        // Under a slice or a claim filter the claims are a share, and a department with a
        // sliver of the slice can have a month of 0.3 claims: $8K over that is the
        // allocation talking, not a rate. Unfiltered counts are whole, so "< 1" is 0.
        of: (m: MonthTotals) => (m.billed < 1 ? null : m.labor / m.billed),
        format: (v: number | null) => money2(v),
        point: (v: number) => `$${Math.round(v)}`,
      },
      {
        // Review #858. The same count cost per billed claim divides by, so it follows
        // the Weighted claims switch, and the month rows already hold only the months
        // each person was paid (the paid-month rule, MEASURE-RULES §3.5). A count: the
        // departments add up to everyone in scope.
        key: 'billed',
        label: label('Billed claims'),
        of: (m: MonthTotals) => m.billed,
        additive: true,
        format: (v: number | null) => int(v),
        point: (v: number) => (v >= 1000 ? `${(v / 1000).toFixed(1)}k` : String(Math.round(v))),
      },
      {
        key: 'labor',
        label: 'Payroll labor',
        of: (m: MonthTotals) => m.labor,
        additive: true,
        format: (v: number | null) => money0(v),
        point: (v: number) => (v >= 1000 ? `$${Math.round(v / 1000)}K` : `$${Math.round(v)}`),
      },
      {
        key: 'res',
        label: 'Resolved claims',
        of: (m: MonthTotals) => m.resolved,
        additive: true,
        format: (v: number | null) => int(v),
        point: (v: number) => (v >= 1000 ? `${Math.round(v / 1000)}k` : String(Math.round(v))),
      },
      {
        key: 'rvb',
        label: 'Claims billed vs resolved %',
        // Standard claims under either basis: resolved claims are never weighted.
        of: (m: MonthTotals) => (m.billedStandard < 1 ? null : m.resolved / m.billedStandard),
        format: (v: number | null) => pct(v),
        point: (v: number) => `${Math.round(v * 100)}%`,
      },
      {
        key: 'ntp',
        label: '0% touch share',
        of: (m: MonthTotals) => (m.touchBase === 0 ? null : m.noTouch / m.touchBase),
        format: (v: number | null) => pct(v),
        point: (v: number) => `${Math.round(v * 100)}%`,
      },
      {
        key: 'tpr',
        label: 'Touches per touched claim',
        of: (m: MonthTotals) => (m.touched === 0 ? null : m.touches / m.touched),
        format: (v: number | null) => dec(v, 2),
        point: (v: number) => v.toFixed(1),
      },
      {
        key: 'wk',
        label: 'Open claims worked in month %',
        of: (m: MonthTotals) => (m.open === 0 ? null : m.openWorked / m.open),
        format: (v: number | null) => pct(v),
        point: (v: number) => `${Math.round(v * 100)}%`,
        needs: 'worklist' as const,
      },
      {
        key: 'facm',
        label: 'Facilities handled (avg per employee)',
        of: (m: MonthTotals) => (m.people === 0 ? null : m.facilities / m.people),
        format: (v: number | null) => dec(v, 1),
        point: (v: number) => v.toFixed(1),
      },
      {
        key: 'coll',
        label: COLLECTION_LABEL,
        of: (m: MonthTotals) => (m.collDen === 0 ? null : m.collNum / m.collDen),
        format: (v: number | null) => pct(v),
        point: (v: number) => `${Math.round(v * 100)}%`,
        needs: 'collectedPct' as const,
      },
    ],
    [label],
  )

  const trendOptions = TREND_METRICS.filter((m) => !m.needs || coverage[m.needs])
  const trend = trendOptions.find((m) => m.key === trendMetric) ?? trendOptions[0]!

  /**
   * The slice the trend is showing: the slicer's own narrowing plus the picked bar.
   * "Break down by" alone does not narrow anything, so it leaves the trend alone.
   */
  const sliceMatch = useMemo<SliceMatch>(() => {
    const m: SliceMatch = {
      state: narrowState || null,
      payer: narrowPayer || null,
      facilityGroup: insideGroup,
    }
    if (selected) m[activeDim] = selected
    return m
  }, [narrowState, narrowPayer, insideGroup, selected, activeDim])
  // Inside the bar's facility groups: the people in scope are already scaled to them.
  const sliceWeight = useMemo(
    () => sliceWeights(crosstab, sliceMatch, barGroups),
    [crosstab, sliceMatch, barGroups],
  )
  // The claim filters are already on the month rows (`reweighted` in data/filters.tsx),
  // so the trend takes the slice's weight alone. Multiplying the claim weight in again
  // counted the filter twice once those rows started carrying it (review #156).
  const trendWeight = sliceWeight ?? undefined

  /**
   * Every line follows the filter bar (through `scope`, whose month rows carry the claim
   * filters' weight) and the slicer (through the slice's weight). See
   * measures/trend.ts for why both are weights and what that makes modelled.
   */
  const monthSeries = useMemo(() => {
    // Each point's claims ride along for the hover tip (review #231): the same people,
    // months and weights, counted instead of divided.
    const billed = (m: MonthTotals) => m.billed
    const pooled = () => ({
      ...pooledTrend(scope.people, scope.months, trend.of, trendWeight),
      counts: pooledTrend(scope.people, scope.months, billed, trendWeight).points,
    })
    // A picked DEPARTMENT bar is the claims credited to that department, by whoever
    // worked them. Lines by each person's own department under it read as "the trend
    // did not change" (review #157), so it is drawn as the one line it is.
    if (selected && activeDim === 'department') {
      return [{ ...pooled(), label: `Claims in ${selected}` }]
    }
    const counts = new Map(
      departmentTrend(derived.departments, scope.people, scope.months, billed, trendWeight).map(
        (l) => [l.key, l.points],
      ),
    )
    const lines = departmentTrend(
      derived.departments,
      scope.people,
      scope.months,
      trend.of,
      trendWeight,
    ).map((l) => ({ ...l, counts: counts.get(l.key) }))
    // The pooled line is what the tiles and the picked bar quote. Not for a count —
    // a total drawn beside its parts flattens them against the axis.
    if (trend.additive || lines.length < 2) return lines
    return [pooled(), ...lines]
  }, [derived.departments, scope.people, scope.months, trend, trendWeight, selected, activeDim])

  /**
   * Modelled when a claim filter is on, or the slice splits anybody — a slice that is
   * all-or-nothing for every person in scope moves no one's figures by an estimate.
   */
  const trendModeled =
    scope.modeled ||
    (sliceWeight !== null &&
      scope.people.some((p) => {
        const w = sliceWeight(p)
        return (w.claims > 0 && w.claims < 1) || (w.labor > 0 && w.labor < 1)
      }))

  const sliceLabel = (v: string, d: Dim) => (d === 'state' ? stateName(v) : v)
  /** The slicer's narrowing, in words, without the picked bar. */
  const narrowText = [
    pageDept.length > 0 && `Department: ${pageDept.join(', ')}`,
    filters.facilityGroup.length > 0 && `Facility group: ${filters.facilityGroup.join(', ')}`,
    narrowState && `State: ${stateName(narrowState)}`,
    narrowPayer && `Payer type: ${narrowPayer}`,
    insideGroup && `Facility group: ${insideGroup}`,
  ]
    .filter(Boolean)
    .join(' · ')
  const pickText = !selected
    ? ''
    : activeDim === 'department'
      ? `Claims in ${selected}`
      : `${dimDef.label}: ${sliceLabel(selected, activeDim)}`
  const trendTitle =
    selected && activeDim === 'department' ? trend.label : `${trend.label} by department`
  const showing = [narrowText, pickText].filter(Boolean).join(' · ')
  const trendNote = [
    'Follows the filter bar and the slicer below',
    ...(trendModeled
      ? [
          sliceWeight
            ? 'modelled: each person’s months split by their share of the slice'
            : scope.modeledNote === GROUP_NOTE
              ? 'modelled: each person’s months split by their share of the facility groups'
              : scope.exact
                ? 'modelled: weighted claims split by each month’s billed share'
                : 'modelled: each person’s months split by their claim-filter share',
        ]
      : []),
  ].join(' · ')

  /**
   * The table view's rows: the lines on the chart, plus "All in scope" under them even
   * where the chart leaves it off (a count). A picked department bar is one line, the
   * claims in that department, and its table is that one row.
   */
  const tableRows = useMemo(() => {
    const additive = Boolean(trend.additive)
    if (selected && activeDim === 'department') return trendTableRows([], monthSeries[0] ?? null, additive)
    const pooled =
      monthSeries.find((l) => l.key === ALL_IN_SCOPE) ??
      pooledTrend(scope.people, scope.months, trend.of, trendWeight)
    return trendTableRows(monthSeries, pooled, additive)
  }, [monthSeries, trend, selected, activeDim, scope.people, scope.months, trendWeight])

  /** The trend metric's twin in the employee × month grid. */
  const gridMetric = MONTH_METRIC_BY_KEY.get(TREND_TO_GRID[trend.key] ?? '')
  // Both in the URL, so Back and a copied link reopen the grid as it was.
  const [gridOpen, setGridOpen] = useUrlState('grid', false, bool)
  // The Ledger's "Cells" switch: the measure itself, or the scorecard points it earns.
  const [gridCells, setGridCells] = useUrlState<'value' | 'points'>('cells', 'value', oneOf(['value', 'points'] as const, 'value'))
  // Peer averages come from the UNFILTERED population, as on the Scorecard page, so
  // narrowing the bar cannot quietly re-cut who each person is compared against.
  const scorer = useMemo(
    () => monthScorer(derived.people, scope.months),
    [derived.people, scope.months],
  )

  const shadedFrom = scope.months.findIndex((m) => dataset.openMonths.includes(m))

  /*
   * The table view (review #858), laid out as the By employee grid is: a frozen name
   * column, a column per month (* not closed), the total where the metric has one.
   * "All in scope" sorts as a blank, so it stays under the departments either way.
   */
  const monthHead = (m: string) => MONTH_LABEL(m) + (dataset.openMonths.includes(m) ? ' *' : '')
  const tableTotal = Boolean(trend.additive)
  const tableColumns: Column<TrendTableRow>[] = [
    {
      key: 'dept',
      header: selected && activeDim === 'department' ? 'Claims in' : 'Department',
      frozen: true,
      width: 200,
      sort: (r) => (r.pooled ? null : r.label),
      render: (r) => (r.pooled ? <b>{r.label}</b> : r.label),
    },
    ...scope.months.map((m, i) => ({
      key: `m${i}`,
      header: monthHead(m),
      align: 'right' as const,
      width: 96,
      sort: (r: TrendTableRow) => (r.pooled ? null : (r.cells[i] ?? null)),
      render: (r: TrendTableRow) => (
        <span className="mono" style={{ fontSize: 'var(--t-table)', fontWeight: r.pooled ? 600 : undefined }}>
          {trend.format(r.cells[i] ?? null)}
        </span>
      ),
    })),
    ...(tableTotal
      ? [
          {
            key: 'total',
            header: 'Total',
            title: 'The months summed',
            align: 'right' as const,
            width: 104,
            divider: true,
            sort: (r: TrendTableRow) => (r.pooled ? null : r.total),
            render: (r: TrendTableRow) => (
              <span className="mono" style={{ fontSize: 'var(--t-table)', fontWeight: r.pooled ? 600 : undefined }}>
                {trend.format(r.total)}
              </span>
            ),
          },
        ]
      : []),
  ]
  /** Exports carry raw values, in the rows' order on screen before any sort. */
  const tableExport: ExportColumn<TrendTableRow>[] = [
    { header: selected && activeDim === 'department' ? 'Claims in' : 'Department', value: (r) => r.label },
    ...scope.months.map((m, i) => ({ header: monthHead(m), value: (r: TrendTableRow) => r.cells[i] ?? null })),
    ...(tableTotal ? [{ header: 'Total', value: (r: TrendTableRow) => r.total }] : []),
  ]
  const trendTable = (maxHeight: string) => (
    <>
      <ExportBar filename={`${trend.key}-by-department.csv`} rows={tableRows} columns={tableExport} />
      <div className="card" style={{ padding: 0, overflow: 'hidden' }} data-testid="trend-table">
        <DataTable
          sortParam="trend.sort"
          storageKey="overview.trend-table"
          rows={tableRows}
          columns={tableColumns}
          rowKey={(r) => r.key}
          maxHeight={maxHeight}
          empty="Nobody in scope has a month to show."
        />
      </div>
      {scope.months.some((m) => dataset.openMonths.includes(m)) && (
        <p className="mono" style={{ fontSize: 'var(--t-micro)', color: 'var(--ink-3)', marginTop: 8 }}>
          * month not closed at export — the rate is real, the volume is not
        </p>
      )}
    </>
  )

  /**
   * Take the picked slice to another page. A department the filter bar knows, with
   * nothing else narrowing the bar, is a real filter. Anything else (a state, payer
   * type, facility group or facility, a narrowed department, and the "Unknown" bar of
   * any of them) goes as the people who work it AND the slice itself, so each
   * person's figures there are their share of it rather than their whole book
   * (review #177; see `Filters.slice`).
   */
  const openSliceIn = (path: '/employees' | '/supervisors') => {
    if (!selected) return
    const plainDept =
      activeDim === 'department' &&
      !narrowState &&
      !narrowPayer &&
      !insideGroup &&
      derived.departments.includes(selected)
    if (plainDept) set('department', [selected])
    else
      set('slice', {
        // The Department filter stays a filter with its own pill; the rest is the slice.
        label:
          [
            narrowState && `State: ${stateName(narrowState)}`,
            narrowPayer && `Payer type: ${narrowPayer}`,
            insideGroup && `Facility group: ${insideGroup}`,
            pickText,
          ]
            .filter(Boolean)
            .join(' · ') || selected,
        employees: slicePeople.map((p) => p.employee),
        match: sliceMatch,
      })
    navigate(path)
  }

  /** The slicer's narrowing and the picked bar, as a drill's `pick` keys. */
  const slicePick = <K extends string>(keys: Partial<Record<Dim, K>>) => {
    const out: Partial<Record<K, string>> = {}
    const put = (d: Dim, v: string | null) => {
      const k = keys[d]
      if (k && v) out[k] = v
    }
    put('state', narrowState)
    put('payer', narrowPayer)
    put('facilityGroup', insideGroup)
    if (selected) put(activeDim, selected)
    return out
  }

  /**
   * The slice's touch rows. Facility group, facility, state and payer type are columns
   * on the touch rows, so Touch detail filters on them as hers does; the department is
   * not, so a department bar takes the people who work it instead.
   */
  const openSliceTouches = () => {
    if (!selected) return
    openTouches({
      employees: slicePeople.map((p) => p.employee),
      label: activeDim === 'state' ? stateName(selected) : selected,
      ...(activeDim !== 'department'
        ? { pick: slicePick({ facilityGroup: 'fg', facility: 'f', state: 's', payer: 'p' } as const) }
        : {}),
    })
  }

  /**
   * The slice's claim rows: facility, state and payer type are claim columns, and so is
   * the department the bars count by — the worker's home department (`wd`). Facility
   * group is not a claim column, but Claim detail has a dropdown for it. Without a pick
   * the slice goes as people, and each brings all their claims, not just the slice's.
   */
  const openSliceClaims = () => {
    if (!selected) return
    openClaims({
      employees: slicePeople.map((p) => p.employee),
      label: activeDim === 'state' ? stateName(selected) : selected,
      pick: slicePick({
        facilityGroup: 'fg',
        facility: 'f',
        state: 's',
        payer: 'p',
        department: 'wd',
      } as const),
    })
  }

  const clearSlicer = () => {
    setDim('facilityGroup')
    setPageDept('')
    setNarrowState('')
    setNarrowPayer('')
    setSelected(null)
    setInsideGroup(null)
  }

  return (
    <>
      <h1 className="page-title">The cost of a claim</h1>
      {unattributed.people > 0 && (
        <p className="body-note" style={{ margin: '4px 0 0' }} data-testid="unattributed-labor">
          Unattributed labor: <b className="num">{money0(unattributed.labor)}</b> paid to{' '}
          {int(unattributed.people)} {unattributed.people === 1 ? 'person' : 'people'} in months they
          billed no claims, and counted in cost per claim.{' '}
          <Link to="/payroll-without-claims">Who, and which months</Link>
        </p>
      )}

      {/*
        No figure strip here. It repeated the masthead's figures directly under them —
        cost per claim, billed claims, payroll labor, collection rate and 0% touch
        share — and was removed in Shayna's 2026-10-02 review ("duplicate to what's on
        top", #8). The masthead carries the five; this page starts at the charts.
      */}

      <section className="section">
        <h2 className="section-heading">Month over month by department</h2>
        <div style={{ marginTop: 12 }}>
          <ChartCard
            // The enlarged view and its PNG have no pills, so the title says the slice.
            title={showing ? `${trendTitle} · ${showing}` : trendTitle}
            note={trendNote}
            controls={
              <>
              <div className="ov-trend-head">
                <h3 className="card-heading" style={{ fontSize: 15, fontWeight: 600 }}>
                  {trendTitle}
                  {trendModeled && (
                    <span
                      className="modeled"
                      style={{ marginLeft: 6, verticalAlign: 0 }}
                      title={
                        sliceWeight
                          ? 'Modelled: the bundle has no month grain by slice, so each person’s months are split by their share of the slice.'
                          : (scope.modeledNote ?? '')
                      }
                    >
                      M
                    </span>
                  )}
                </h3>
                <span
                  className="mono"
                  data-testid="trend-scope"
                  style={{ fontSize: 12, color: 'var(--ink-2)' }}
                >
                  {showing ? 'Showing' : 'Everyone in scope'}
                  {narrowText && (
                    <>
                      {' '}
                      <span className="pill-sel">
                        {narrowText}
                        <button
                          type="button"
                          title="Clear the slicer's department, state, payer type and facility group"
                          aria-label={`Clear ${narrowText}`}
                          onClick={() => {
                            setPageDept('')
                            setNarrowState('')
                            setNarrowPayer('')
                            setInsideGroup(null)
                            setSelected(null)
                            if (insideGroup) setDim('facilityGroup')
                          }}
                        >
                          ×
                        </button>
                      </span>
                    </>
                  )}
                  {pickText && (
                    <>
                      {' '}
                      <span className="pill-sel">
                        {pickText}
                        <button
                          type="button"
                          title="Clear the picked bar"
                          aria-label={`Clear ${pickText}`}
                          onClick={() => setSelected(null)}
                        >
                          ×
                        </button>
                      </span>
                    </>
                  )}
                </span>
              </div>
              <div
                className="ov-slicer-controls"
                style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap', marginTop: 10 }}
              >
                <div style={{ width: 236 }}>
                  <Select
                    label="Metric"
                    value={trend.key}
                    options={trendOptions.map((m) => ({ value: m.key, label: m.label }))}
                    onChange={setTrendMetric}
                  />
                </div>
                <div role="radiogroup" aria-label="Show as" className="td-seg" style={{ marginBottom: 5 }}>
                  {(
                    [
                      ['chart', 'Chart'],
                      ['table', 'Table'],
                    ] as const
                  ).map(([v, name]) => (
                    <button
                      key={v}
                      type="button"
                      role="radio"
                      aria-checked={trendView === v}
                      onClick={() => trendView !== v && setTrendView(v)}
                    >
                      {name}
                    </button>
                  ))}
                </div>
                {gridMetric && (
                  <button
                    type="button"
                    className="byemp"
                    style={{ marginBottom: 5 }}
                    onClick={() => setGridOpen(true)}
                  >
                    By employee
                  </button>
                )}
              </div>
              </>
            }
            png={trendView === 'chart'}
            children={
              trendView === 'table' ? (
                trendTable('420px')
              ) : (
              <LineChart
                series={monthSeries}
                labels={scope.months.map(MONTH_LABEL)}
                shadedFrom={shadedFrom >= 0 ? shadedFrom : undefined}
                format={trend.format}
                formatPoint={trend.point}
                height={350}
                labelEverySeries
                countLabel={label('billed claims')}
              />
              )
            }
            large={
              trendView === 'table' ? (
                trendTable('calc(100vh - 220px)')
              ) : (
              <LineChart
                series={monthSeries}
                labels={scope.months.map(MONTH_LABEL)}
                shadedFrom={shadedFrom >= 0 ? shadedFrom : undefined}
                format={trend.format}
                formatPoint={trend.point}
                // The full-page view: as tall as the window leaves after its header.
                height={Math.max(420, window.innerHeight - 220)}
                labelEverySeries
                countLabel={label('billed claims')}
              />
              )
            }
          />
        </div>

        {gridOpen && gridMetric && (
          <Scrim onClose={() => setGridOpen(false)}>
            <div
              className="card"
              style={{ width: 'min(1300px, 96vw)', maxHeight: '90vh', overflow: 'auto' }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
                <h3 className="section-heading">{trend.label} by employee</h3>
                <button
                  type="button"
                  aria-label="Close"
                  onClick={() => setGridOpen(false)}
                  style={{
                    marginLeft: 'auto',
                    background: 'none',
                    border: 0,
                    cursor: 'pointer',
                    fontSize: 16,
                    color: 'var(--ink-3)',
                  }}
                >
                  ×
                </button>
              </div>
              {gridMetric.scored && (
                <div style={{ width: 200, marginTop: 12 }}>
                  <Select
                    label="Cells"
                    value={gridCells}
                    options={[
                      { value: 'value', label: 'Values' },
                      { value: 'points', label: 'Scorecard points' },
                    ]}
                    onChange={(v) => setGridCells(v as 'value' | 'points')}
                  />
                </div>
              )}
              <div style={{ marginTop: 14 }}>
                <EmployeeMonthGrid
                  // Remount on switch so the points view opens best-first rather than
                  // inheriting the value view's low-is-better ascending sort.
                  key={gridCells}
                  metric={gridMetric}
                  mode={gridMetric.scored ? gridCells : 'value'}
                  scorer={scorer}
                  people={scope.people.filter((p) => p.claims > 0)}
                  months={scope.months}
                  openMonths={dataset.openMonths}
                  volume={volume}
                  onOpenPerson={(n) => {
                    setGridOpen(false)
                    openPerson(n)
                  }}
                />
              </div>
            </div>
          </Scrim>
        )}
      </section>

      <section className="section">
        <h2 className="section-heading">Cost per claim, sliced</h2>
        <PopOut title="Cost per claim, sliced" style={{ marginTop: 14 }}>
          {({ button, enlarged }) => (
            <div className="card">
              {/* The slicer's own controls live inside the card, ruled off from the chart,
                  as in the Ledger: they narrow THIS chart, not the page. */}
              <div
                className="ov-slicer-controls"
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '10px 14px',
                  alignItems: 'flex-end',
                  paddingBottom: 14,
                  marginBottom: 14,
                  borderBottom: '1px solid var(--rule)',
                }}
              >
                <div style={{ minWidth: 190, paddingRight: 16, marginRight: 4, borderRight: '1px solid var(--rule)' }}>
                  <SearchPick
                    label="Break down by — changes the chart"
                    lead
                    value={dim}
                    options={DIMS.map((d) => ({ value: d.value, label: d.label }))}
                    onChange={(v) => {
                      setDim(v as Dim)
                      setSelected(null)
                      setInsideGroup(null)
                    }}
                  />
                </div>
                <div style={{ minWidth: 150 }}>
                  {/* The filter bar's Department, not a second one: see `setPageDept`. */}
                  <SearchPick
                    label="Department · whole page"
                    value={pageDept.length > 1 ? SEVERAL : (pageDept[0] ?? '')}
                    allValue=""
                    allLabel="All departments"
                    options={[
                      ...(pageDept.length > 1
                        ? [{ value: SEVERAL, label: `${pageDept.length} departments (filter bar)` }]
                        : []),
                      ...derived.departments.map((d) => ({ value: d, label: d })),
                    ]}
                    onChange={(v) => {
                      if (v === SEVERAL) return
                      setPageDept(v)
                      setSelected(null)
                    }}
                  />
                </div>
                <div style={{ minWidth: 150 }}>
                  <SearchPick
                    label="State"
                    value={narrowState}
                    allValue=""
                    allLabel="All states"
                    // The code rides along, so "FL" finds Florida as "flor" does.
                    options={derived.states
                      .map((d) => ({ value: d, label: stateName(d), detail: stateName(d) === d ? null : d }))
                      .sort((a, b) => a.label.localeCompare(b.label))}
                    onChange={(v) => {
                      setNarrowState(v)
                      setSelected(null)
                    }}
                  />
                </div>
                <div style={{ minWidth: 150 }}>
                  <SearchPick
                    label="Payer type"
                    help={DIMENSION_HELP.payerType}
                    value={narrowPayer}
                    allValue=""
                    allLabel="All payer types"
                    options={derived.payers.map((d) => ({ value: d, label: d }))}
                    onChange={(v) => {
                      setNarrowPayer(v)
                      setSelected(null)
                    }}
                  />
                </div>
                <div style={{ minWidth: 150 }}>
                  <SearchPick
                    label="Facility group"
                    value={insideGroup ?? ''}
                    allValue=""
                    allLabel="All facility groups"
                    options={derived.facilityGroups.map((d) => ({ value: d, label: d }))}
                    onChange={(v) => {
                      setInsideGroup(v || null)
                      setSelected(null)
                    }}
                  />
                </div>
                <button
                  type="button"
                  onClick={clearSlicer}
                  className="reset-btn"
                  style={{ marginLeft: 'auto', height: 29 }}
                >
                  Clear
                </button>
                {button}
              </div>

              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'baseline',
                  gap: '4px 12px',
                  marginBottom: 14,
                }}
              >
                <h3 className="card-heading" style={{ fontSize: 15, fontWeight: 600 }}>
                  {liveTitle}
                </h3>
                <span className="mono" style={{ fontSize: 12, color: 'var(--ink-2)' }}>
                  {liveCaption}
                </span>
              </div>
              {insideGroup && (
                <div className="crumb">
                  <button
                    type="button"
                    onClick={() => {
                      setInsideGroup(null)
                      setSelected(null)
                      setDim('facilityGroup')
                    }}
                  >
                    ← All facility groups
                  </button>
                  <span>
                    <FacilityLink group={insideGroup} />
                  </span>
                </div>
              )}

              {selected && (
                <div className="selbar">
                  <div>
                    <b>{activeDim === 'state' ? stateName(selected) : selected}</b>
                    <span style={{ color: 'var(--ink-3)', fontSize: 11 }}>
                      {' '}·{' '}
                      {pickedNoClaim
                        ? `${int(slicePeople.length)} people have payroll that landed on no claim — no facility, state or payer, so no cost per claim of its own`
                        : `${int(slicePeople.length)} people work these claims`}
                    </span>
                  </div>
                  <div className="selbar-acts">
                    {activeDim === 'facilityGroup' && !pickedNoClaim && (
                      <button
                        type="button"
                        onClick={() => {
                          setInsideGroup(selected)
                          setSelected(null)
                        }}
                      >
                        Its facilities
                      </button>
                    )}
                    {/* Review #226: a facility or group always opens on Facilities. */}
                    {(activeDim === 'facilityGroup' || activeDim === 'facility') && !pickedNoClaim && (
                      <button
                        type="button"
                        onClick={() =>
                          openFacility(activeDim === 'facility' ? { facility: selected } : { group: selected })
                        }
                      >
                        Open on Facilities
                      </button>
                    )}
                    <button type="button" onClick={() => openSliceIn('/employees')}>
                      Open in Employees
                    </button>
                    <button type="button" onClick={() => openSliceIn('/supervisors')}>
                      Open in Supervisors
                    </button>
                    <button
                      type="button"
                      className={rowsOpen ? 'on' : undefined}
                      onClick={() => setRowsOpen(!rowsOpen)}
                      title="The rows this bar adds up — by person, and for labor on no claim by month"
                    >
                      Labor rows
                    </button>
                    {/* The same pile by person and month, on its own page (#230). */}
                    {pickedNoClaim && (
                      <button type="button" onClick={() => navigate('/payroll-without-claims')}>
                        Payroll without claims
                      </button>
                    )}
                    {/* No claim rows or touch rows exist for labor on no claim. */}
                    {!pickedNoClaim && (
                      <>
                        <button type="button" onClick={openSliceClaims}>
                          Claim rows
                        </button>
                        <button type="button" onClick={openSliceTouches}>
                          Touch rows
                        </button>
                      </>
                    )}
                    <button type="button" className="ghost" onClick={() => setSelected(null)}>
                      Clear
                    </button>
                  </div>
                </div>
              )}

              {selected && rowsOpen && (
                <SliceLaborRows
                  // A fresh panel per bar, so its By month / By person choice resets.
                  key={`${activeDim}|${selected}`}
                  title={pickedNoClaim ? NO_CLAIM : activeDim === 'state' ? stateName(selected) : selected}
                  cells={cells}
                  months={noClaimMonths}
                  narrowed={derived.narrowed}
                  openPerson={(name) => openPerson(name)}
                />
              )}

              {(() => {
                // The enlarged view draws every bar; the card, the largest TOP_BARS unless
                // Show all is on. Unknown and no-claim are drawn either way, under the rule.
                const view = enlarged ? shownEvery : shownTop
                const drawn = view.ranked.length + view.pinned.length
                return (
                  <>
                    <RankBars
                      items={view.ranked}
                      pinned={view.pinned}
                      pinnedLabel={
                        activeDim === 'facilityGroup' || activeDim === 'facility'
                          ? 'Not a facility / not resolved'
                          : 'No claim / not resolved'
                      }
                      selected={selected}
                      onSelect={(k) => setSelected(selected === k ? null : k)}
                      clickHint="Click to show this in the trend above, and who works it"
                      labelWidth={activeDim === 'facility' ? 250 : 200}
                      formatAxis={moneyK}
                      heads={{ count: label('Billed claims'), right: 'Cost per claim' }}
                      sort={barSort && { key: barSort.key === 'claims' ? 'count' : 'right', dir: barSort.dir }}
                      onSort={(k) => setRawBarSort(nextBarSort(barSort, k === 'count' ? 'claims' : 'cpc'))}
                    />
                    {view.cut && (
                      <div
                        data-testid="slice-cutoff"
                        style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 9 }}
                      >
                        <p style={{ fontSize: 11.5, color: 'var(--ink-3)', margin: 0 }}>
                          {drawn < sliceCount
                            ? `Showing ${int(drawn)} of ${int(sliceCount)}: ` +
                              (barSort ? `the first ${TOP_BARS} ${barSortPhrase(barSort)}` : `the ${TOP_BARS} largest by payroll labor`) +
                              (view.ranked.length > TOP_BARS ? ', the picked one' : '') +
                              (view.pinned.length ? ', and those below the line.' : '.')
                            : `Showing all ${int(sliceCount)} ${barSortPhrase(barSort)}.`}
                        </p>
                        {!enlarged && (
                          <button
                            type="button"
                            className="btn"
                            onClick={() => setShowAllBars(!showAllBars)}
                          >
                            {showAllBars ? `Top ${TOP_BARS}` : `Show all ${int(sliceCount)}`}
                          </button>
                        )}
                      </div>
                    )}
                  </>
                )
              })()}

            </div>
          )}
        </PopOut>
      </section>

    </>
  )
}
